import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { App } from './App';
import './i18n';
import './styles/index.css';
import { installErrorReporter } from './lib/errorReporter';
import { ApiError } from './lib/api';
import { Toaster, toast } from './lib/toast';

// Build marker — diagnostic per verificare bundle live.
// Cambia stringa ad ogni rebuild forzato per garantire hash unico.
// Build: 2026-06-26t-notif-prefs-member
// eslint-disable-next-line no-console
console.log('[Manteco] bundle 2026-06-26t · notif-prefs-member · main.tsx loaded');

// Chunk stantio dopo un deploy: con il code-splitting per rotta, una tab
// aperta da PRIMA di un deploy può chiedere un chunk (hash vecchio) che su
// Netlify non esiste più → "Failed to fetch dynamically imported module".
// Vite emette `vite:preloadError` esattamente in quel caso: ricarichiamo la
// pagina UNA volta (l'index nuovo porta gli hash giusti). Guardia anti-loop:
// se il reload non risolve (es. offline), non insistiamo.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'manteco.chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 15_000) return; // già ricaricato da poco → lascia fallire
  event.preventDefault(); // sopprimi il TypeError: gestiamo noi
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

const queryClient = new QueryClient({
  // Feedback globale su OGNI azione (mutation):
  // - errore → toast rosso automatico (niente più fallimenti silenziosi),
  //   salvo le mutation che lo gestiscono da sé (meta.silentError).
  // - successo → toast verde SE la mutation fornisce meta.successMessage.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silentError) return;
      let msg = 'Errore di rete';
      if (error instanceof ApiError) {
        msg = error.message.startsWith('HTTP ')
          ? 'Si è verificato un errore, riprova'
          : error.message;
      }
      toast.error(msg);
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const m = mutation.meta?.successMessage;
      if (m) toast.success(m);
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster />
    </QueryClientProvider>
  </StrictMode>,
);

// Service Worker (PWA). Solo in production: in dev (vite-dev-server con
// HMR) il SW interferisce con il live reload.
installErrorReporter();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[Manteco] service worker registration failed:', err);
      });
  });
}
